import React, { useEffect, useState } from 'react';
import { Checkbox, Form, InputNumber, Select, Input, Switch } from 'antd';
import { useTranslation } from 'react-i18next';

import { BasicFormWrapper } from 'container/styled';
import { Modal } from 'components/modals/antd-modals';
import { Button } from 'components/buttons/buttons';
import openNotificationWithIcon from 'utility/notification';
import { CollectionChannelConfig, CollectionActionTypeConfig } from 'constants/constants';
import {
  debtCollectionApi,
  DebtCollectionRuleDto,
  DebtCollectionTemplateDto,
  CollectionActionType,
  CollectionChannel,
} from '../debtCollectionApi';

interface RuleFormModalProps {
  visible: boolean;
  editMode: boolean;
  rule: DebtCollectionRuleDto | null;
  templates: DebtCollectionTemplateDto[];
  onCancel: () => void;
  onSuccess: () => void;
}

const RuleFormModal: React.FC<RuleFormModalProps> = ({
  visible,
  editMode,
  rule,
  templates,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState<CollectionActionType>(CollectionActionType.SendMessage);

  useEffect(() => {
    if (rule && editMode) {
      form.setFieldsValue(rule);
      setActionType(rule.actionType ?? CollectionActionType.SendMessage);
    } else {
      form.resetFields();
      setActionType(CollectionActionType.SendMessage);
    }
  }, [rule, editMode, form]);

  const handleSubmit = async (values: Partial<DebtCollectionRuleDto>) => {
    setLoading(true);
    try {
      if (editMode && rule?.id) {
        await debtCollectionApi.updateRule(rule.id, values);
      } else {
        await debtCollectionApi.createRule(values);
      }
      openNotificationWithIcon(
        'success',
        t(
          `debt-collection:rules.${editMode ? 'updated' : 'created'}`,
          `Rule ${editMode ? 'updated' : 'created'} successfully`,
        ),
      );
      onSuccess();
    } catch (error) {
      console.error('Error saving rule:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      type="primary"
      title={t(`debt-collection:rules.${editMode ? 'edit' : 'add'}`, `${editMode ? 'Edit' : 'Add'} Rule`)}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
    >
      <BasicFormWrapper>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label={t('debt-collection:rules.name', 'Rule Name')}
            name="name"
            rules={[{ required: true, message: t('debt-collection:rules.name-required', 'Rule name is required') }]}
          >
            <Input placeholder={t('debt-collection:rules.name-placeholder', 'e.g., First Reminder')} />
          </Form.Item>

          <Form.Item
            label={t('debt-collection:rules.days-overdue', 'Days Overdue')}
            name="daysOverdue"
            rules={[{ required: true, message: t('debt-collection:rules.days-required', 'Days overdue is required') }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label={t('debt-collection:rules.action-type', 'Action Type')}
            name="actionType"
            rules={[{ required: true }]}
          >
            <Select onChange={(val) => setActionType(val)}>
              {Object.entries(CollectionActionTypeConfig).map(([key, cfg]) => (
                <Select.Option key={key} value={Number(key)}>
                  {cfg.title}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('debt-collection:rules.channel', 'Channel')}
            name="channel"
            rules={[{ required: true }]}
          >
            <Select>
              {Object.entries(CollectionChannelConfig).map(([key, cfg]) => (
                <Select.Option key={key} value={Number(key)}>
                  {cfg.title}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('debt-collection:rules.template', 'Template')}
            name="templateId"
          >
            <Select allowClear placeholder={t('debt-collection:rules.select-template', 'Select template (optional)')}>
              {templates.map((tmpl) => (
                <Select.Option key={tmpl.id} value={tmpl.id}>
                  {tmpl.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {actionType === CollectionActionType.Webhook && (
            <Form.Item
              label={t('debt-collection:rules.webhook-url', 'Webhook URL')}
              name="webhookUrl"
              rules={[{ required: true, type: 'url', message: t('debt-collection:rules.webhook-url-required', 'Valid URL is required') }]}
            >
              <Input placeholder="https://..." />
            </Form.Item>
          )}

          {actionType === CollectionActionType.EscalateToManager && (
            <Form.Item
              label={t('debt-collection:rules.escalate-to', 'Escalate To (User ID)')}
              name="escalateToUserId"
            >
              <Input placeholder={t('debt-collection:rules.escalate-placeholder', 'User ID to notify')} />
            </Form.Item>
          )}

          <Form.Item name="flagDealAsCollections" valuePropName="checked">
            <Checkbox>
              {t('debt-collection:rules.flag-deal', 'Flag deal as Collections')}
            </Checkbox>
          </Form.Item>

          <Form.Item
            label={t('debt-collection:rules.active', 'Active')}
            name="isActive"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={onCancel}>
              {t('global:cancel', 'Cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editMode ? t('global:update', 'Update') : t('global:create', 'Create')}
            </Button>
          </div>
        </Form>
      </BasicFormWrapper>
    </Modal>
  );
};

export default RuleFormModal;
